import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ErrorMessage, FieldError, Spinner } from '@/components/ui'
import { ApiError, api } from '@/lib/api'
import type { RegisterResponse } from '@/lib/types'
import { useAuthStore } from '@/stores/auth'

const PASSWORD_MIN_LENGTH = 10

export function RegisterPage() {
  const navigate = useNavigate()
  const setAccessToken = useAuthStore((state) => state.setAccessToken)
  const setSession = useAuthStore((state) => state.setSession)

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [workspaceName, setWorkspaceName] = useState('')

  const register = useMutation({
    mutationFn: () =>
      api.post<RegisterResponse>(
        '/auth/register',
        {
          email,
          password,
          full_name: fullName,
          // Пустое поле означает «название по умолчанию», решает backend
          ...(workspaceName.trim() ? { workspace_name: workspaceName.trim() } : {}),
        },
        { skipWorkspace: true },
      ),
    onSuccess: (data) => {
      setAccessToken(data.tokens.access_token)
      setSession(data.user, data.workspace ? [data.workspace] : [])
      void navigate('/', { replace: true })
    },
  })

  const fieldErrors = register.error instanceof ApiError ? register.error.fieldErrors : {}
  const isPasswordShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-600 text-base font-bold text-white">
            RF
          </span>
          <h1 className="text-2xl font-semibold text-slate-900">Регистрация</h1>
          <p className="mt-1 text-sm text-slate-500">
            Рабочее пространство создаётся сразу — можно начинать с загрузки данных
          </p>
        </div>

        <form
          className="card space-y-4 p-6"
          onSubmit={(event) => {
            event.preventDefault()
            register.mutate()
          }}
        >
          <div>
            <label className="label" htmlFor="full_name">
              Ваше имя
            </label>
            <input
              id="full_name"
              type="text"
              autoComplete="name"
              className="input"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Иван Петров"
            />
            <FieldError message={fieldErrors.full_name} />
          </div>

          <div>
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              className={`input ${fieldErrors.email ? 'input-error' : ''}`}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="director@company.ru"
            />
            <FieldError message={fieldErrors.email} />
          </div>

          <div>
            <label className="label" htmlFor="password">
              Пароль
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              className={`input ${fieldErrors.password || isPasswordShort ? 'input-error' : ''}`}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            {/* Требование показывается заранее, а не после отправки формы */}
            <p className="mt-1 text-xs text-slate-500">
              Не короче {PASSWORD_MIN_LENGTH} символов. Длина важнее спецсимволов.
            </p>
            <FieldError message={fieldErrors.password} />
          </div>

          <div>
            <label className="label" htmlFor="workspace_name">
              Название компании
              <span className="ml-1 font-normal text-slate-400">(необязательно)</span>
            </label>
            <input
              id="workspace_name"
              type="text"
              className="input"
              value={workspaceName}
              onChange={(event) => setWorkspaceName(event.target.value)}
              placeholder="ООО Ромашка"
            />
            <FieldError message={fieldErrors.workspace_name} />
          </div>

          <ErrorMessage error={register.error} />

          <button type="submit" className="btn-primary w-full" disabled={register.isPending}>
            {register.isPending && <Spinner className="h-4 w-4" />}
            Создать аккаунт
          </button>

          <p className="text-center text-sm text-slate-500">
            Уже есть аккаунт?{' '}
            <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
              Войти
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
